import { validationRules } from './constant';

/**
 * Validates the search input in real-time as the user types.
 * This function provides immediate feedback on the input's format based on the selected category.
 * @param {string} value - The current input value from the text field.
 * @param {string | null | undefined} category - The selected search category (e.g., 'loan_id', 'borrower_phone_number').
 * @returns {string | null} An error message string if validation fails, otherwise null.
 */
export const validateSearchInput = (
  value: string,
  category: string | null | undefined
): string | null => {
  if (!category) {
    return null;
  }

  const rule = validationRules[category];

  if (rule && rule.regex.test(value)) {
    return rule.message;
  }

  return null;
};

interface ProcessResult {
  processedText: string | null;
  error: string | null;
}

/**
 * Processes, sanitizes, and performs final validation on the search text before submission.
 * This function handles transformations like trimming whitespace or formatting phone numbers.
 * @param {string} searchText - The search text to be processed.
 * @param {string | null | undefined} category - The selected search category.
 * @returns {ProcessResult} An object containing the processed text and any validation error.
 */
export const processAndValidateSearchText = (
  searchText: string,
  category: string | null | undefined
): ProcessResult => {
  const trimmedText = searchText.trim();
  if (!trimmedText) {
    return { processedText: null, error: null };
  }

  if (category === 'borrower_phone_number') {
    const sanitizedNumber = trimmedText.replace(/\D/g, '');
    const finalNumber = sanitizedNumber.startsWith('1')
      ? sanitizedNumber.substring(1)
      : sanitizedNumber;

    if (finalNumber.length !== 10) {
      return { processedText: finalNumber, error: 'Phone number must contain exactly 10 digits' };
    }
    return { processedText: finalNumber, error: null };
  }

  return { processedText: trimmedText, error: null };
};
